import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, CheckCircle2, Clock, CloudOff, FileUp, Link2Off, TimerOff } from 'lucide-react';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  ASSESSMENT_FILE_ACCEPT,
  ASSESSMENT_FILE_MAX_BYTES,
  ASSESSMENT_FILE_RULE,
  type TestAnswer,
  type TestQuestionView,
  type TestSessionView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Input, Textarea } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { formatDateTime } from '../../../lib/format';
import { testSessionKey, uploadTestAnswer, useTestSession, useTestStep, type TestSession } from '../api/test-player-api';
import { formatClock, useCountdown } from '../hooks/use-countdown';
import { useTestAnswers, type SaveState, type TestAnswers } from '../hooks/use-test-answers';
import styles from './TestPlayerPage.module.css';

/** Under this the clock turns amber; under the second, red (06-qol §11 status colours). */
const WARN_SECONDS = 120;
const URGENT_SECONDS = 30;
const SHORT_ANSWER_MAX = 500;
const LONG_ANSWER_MAX = 20_000;
/** The server's code for a switched-off module (§L28): the test is gone, not unreachable. */
const MODULE_OFF = 'module_off';

/**
 * ASM-03 — the candidate's test, from N-20's link, with no account (D16). Every answer
 * is saved as it is given and kept on the phone until the server has it; the clock is
 * the server's, so leaving and coming back — on this device or another — finds it
 * where it would have been (S17 Q7). What is right is never sent here.
 */
export function TestPlayerPage({ token }: { readonly token: string }): ReactNode {
  const session = useTestSession(token);

  if (token === '') return <Notice icon={Link2Off} title="This link is incomplete" body="Open the link from your email again, or copy all of it into the address bar." />;
  if (session.isPending) {
    return (
      <Frame>
        <SkeletonLines count={8} />
      </Frame>
    );
  }
  if (session.error) {
    if (session.error.status === 404) return <Notice icon={Link2Off} title="This link doesn't work" body={session.error.message} />;
    // Assessments switched off: trying again can never help, so it reads as a withdrawn test.
    if (session.error.status === 403 && session.error.code === MODULE_OFF) return <Notice icon={Link2Off} title="This test is no longer available" body="The recruitment team will be in touch about what happens next." />;
    return <Notice icon={CloudOff} title="We couldn't reach the test" body="Check your connection and try again. Nothing you answered has been lost." action={<Button variant="solid" onClick={() => void session.refetch()}>Try again</Button>} />;
  }
  return <Session token={token} view={session.data} />;
}

function Session({ token, view }: { readonly token: string; readonly view: TestSession }): ReactNode {
  switch (view.state) {
    case 'ready':
      return <Intro token={token} view={view} />;
    case 'in_progress':
      return <Running token={token} view={view} />;
    case 'submitted':
      return (
        <Notice
          icon={CheckCircle2}
          tone="success"
          title="Your test is in"
          body={`Thank you, ${view.candidateFirstName}. Your answers to ${view.testName} for ${view.jobTitle} have reached the ${view.companyName} recruitment team, who will be in touch about what happens next.`}
        />
      );
    case 'expired':
      return (
        <Notice
          icon={TimerOff}
          title="This test link has expired"
          body={`The link to ${view.testName} for ${view.jobTitle} stopped working${view.expiresAt ? ` on ${formatDateTime(view.expiresAt)}` : ''}. Reply to the email it came in to ask whether a new one can be sent.`}
        />
      );
    case 'withdrawn':
      return <Notice icon={Link2Off} title="This test is no longer available" body={`The ${view.companyName} recruitment team will be in touch about ${view.jobTitle}.`} />;
  }
}

// --- Before starting ----------------------------------------------------------------------

function Intro({ token, view }: { readonly token: string; readonly view: TestSessionView }): ReactNode {
  const step = useTestStep(token);
  const [isStarting, setIsStarting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const questionCount = view.sections.reduce((sum, section) => sum + section.questionCount, 0);

  async function start(): Promise<void> {
    setIsStarting(true);
    setProblem(null);
    try {
      await step('start');
    } catch (error) {
      setProblem(errorMessage(error));
      setIsStarting(false);
    }
  }

  return (
    <Frame company={view.companyName}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>
          {view.jobTitle} · {view.companyName}
        </p>
        <h1 className={styles.title}>Hello {view.candidateFirstName}</h1>
        <p className={styles.lead}>
          This is {view.testName}: {questionCount} question{questionCount === 1 ? '' : 's'} in {view.totalMinutes} minutes.
        </p>
        {view.description && <p className={styles.body}>{view.description}</p>}
        {view.sectionCount > 1 && (
          <ol className={styles.sections}>
            {view.sections.map((section) => (
              <li key={section.name} className={styles.sectionRow}>
                <span className={styles.sectionName}>{section.name}</span>
                <span className={styles.meta}>
                  {section.questionCount} question{section.questionCount === 1 ? '' : 's'} · {section.minutes} min
                </span>
              </li>
            ))}
          </ol>
        )}
        <ul className={styles.rules}>
          <li>The clock starts when you press Start and keeps running if you leave.</li>
          <li>Every answer is saved as you give it. If your connection drops, open the link again to carry on.</li>
          {view.sectionCount > 1 && <li>Each section has its own time. When it runs out, the next section starts, and you can't go back.</li>}
          <li>When time runs out, what you have answered is sent automatically.</li>
        </ul>
        {view.expiresAt && <p className={styles.meta}>The link works until {formatDateTime(view.expiresAt)}.</p>}
        {problem && <p className={styles.error}>{problem}</p>}
        <Button variant="solid" size="lg" block isPending={isStarting} onClick={() => void start()}>
          Start the test
        </Button>
      </section>
    </Frame>
  );
}

// --- The test itself ----------------------------------------------------------------------

function Running({ token, view }: { readonly token: string; readonly view: TestSession }): ReactNode {
  const section = view.section;
  const step = useTestStep(token);
  const saving = useTestAnswers(token, section);
  const secondsLeft = useCountdown(section?.deadlineAt ?? null, Date.parse(view.serverNow) - view.receivedAt);
  const [isMoving, setIsMoving] = useState(false);
  const [confirm, setConfirm] = useState<AnchorRect | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const isOnline = useOnline();
  const timedOutFor = useRef<number | null>(null);
  const isLast = section !== null && section.number === view.sectionCount;

  async function moveOn(): Promise<void> {
    setConfirm(null);
    setIsMoving(true);
    setProblem(null);
    // Everything typed goes first; the server takes no answers for a section once it is left.
    await saving.flush();
    try {
      await step(isLast ? 'submit' : 'next-section');
      window.scrollTo({ top: 0 });
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setIsMoving(false);
    }
  }

  // Time up: send what is there and move on, once per section.
  const moveOnRef = useRef(moveOn);
  useEffect(() => {
    moveOnRef.current = moveOn;
  });
  const sectionNumber = section?.number ?? null;
  useEffect(() => {
    if (secondsLeft !== 0 || sectionNumber === null || timedOutFor.current === sectionNumber) return;
    timedOutFor.current = sectionNumber;
    void moveOnRef.current();
  }, [secondsLeft, sectionNumber]);

  if (!section) return <Notice icon={Clock} title="Moving on" body="Your next section is starting." />;
  const answered = section.questions.filter((question) => isAnswered(saving.answers.get(question.id) ?? null, question)).length;
  const unanswered = section.questions.length - answered;
  const clockTone = clockToneOf(secondsLeft);

  return (
    <Frame company={view.companyName}>
      <div className={styles.bar} role="status" aria-live="off">
        <div className={styles.barText}>
          <span className={styles.barTitle}>{view.testName}</span>
          <span className={styles.meta}>
            {view.sectionCount > 1 ? `Section ${section.number} of ${view.sectionCount} · ` : ''}
            {answered} of {section.questions.length} answered
          </span>
        </div>
        <span className={`${styles.clock} ${clockTone}`} aria-label={secondsLeft === null ? 'Time left' : `${formatClock(secondsLeft)} left in this section`}>
          <Clock size={16} aria-hidden />
          {secondsLeft === null ? '—' : formatClock(secondsLeft)}
        </span>
      </div>
      {!isOnline && (
        <p className={styles.offline} role="alert">
          <CloudOff size={16} aria-hidden /> You're offline. Your answers are kept on this phone and will be saved when you reconnect. The clock keeps running.
        </p>
      )}

      <section className={styles.card}>
        {view.sectionCount > 1 && <h1 className={styles.sectionTitle}>{section.name}</h1>}
        {section.instructions && <p className={styles.body}>{section.instructions}</p>}
        <ol className={styles.questions}>
          {section.questions.map((question, index) => (
            <li key={question.id} className={styles.question}>
              <Question number={index + 1} token={token} question={question} saving={saving} />
            </li>
          ))}
        </ol>
      </section>

      <div className={styles.footer}>
        {problem && <p className={styles.error}>{problem}</p>}
        {saving.unsavedCount > 0 && isOnline && <p className={styles.meta}>Saving your answers…</p>}
        <Button
          variant="solid"
          size="lg"
          block
          isPending={isMoving}
          onClick={(event: MouseEvent<HTMLButtonElement>) => setConfirm(anchorFrom(event.currentTarget))}
        >
          {isLast ? 'Submit the test' : 'Next section'}
        </Button>
      </div>

      <Popover anchor={confirm} onClose={() => setConfirm(null)} label={isLast ? 'Submit the test' : 'Next section'}>
        <div className={styles.confirm}>
          <p className={styles.confirmTitle}>{isLast ? 'Submit your test?' : 'Go to the next section?'}</p>
          <p className={styles.body}>
            {unanswered > 0 ? `${unanswered} question${unanswered === 1 ? ' is' : 's are'} not answered. ` : ''}
            {isLast ? "You can't change your answers after submitting." : "You can't come back to this section."}
          </p>
          <div className={styles.confirmActions}>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Keep going
            </Button>
            <Button variant="solid" onClick={() => void moveOn()}>
              {isLast ? 'Submit' : 'Next section'}
            </Button>
          </div>
        </div>
      </Popover>
    </Frame>
  );
}

function Question({ number, token, question, saving }: { readonly number: number; readonly token: string; readonly question: TestQuestionView; readonly saving: TestAnswers }): ReactNode {
  const answer = saving.answers.get(question.id) ?? null;
  const chosen = answer?.kind === 'choice' ? answer.optionIds : [];
  const text = answer?.kind === 'text' ? answer.text : '';
  const labelId = `question-${question.id}`;

  return (
    <fieldset className={styles.fieldset} aria-labelledby={labelId}>
      <div className={styles.questionHead}>
        <span id={labelId} className={styles.questionText}>
          <span className={styles.number}>{number}.</span> {question.text}
        </span>
        <span className={styles.meta}>
          {question.points} mark{question.points === 1 ? '' : 's'}
        </span>
      </div>
      {question.type === 'single_select' && (
        <div className={styles.checks} role="radiogroup" aria-labelledby={labelId}>
          {(question.options ?? []).map((option) => (
            <ChoiceCard key={option.id} kind="radio" name={question.id} isOn={chosen.includes(option.id)} label={option.text} onChange={() => saving.change(question.id, { kind: 'choice', optionIds: [option.id] }, false)} />
          ))}
        </div>
      )}
      {question.type === 'multi_select' && (
        <div className={styles.checks}>
          <span className={styles.meta}>Choose all that apply.</span>
          {(question.options ?? []).map((option) => (
            <ChoiceCard
              key={option.id}
              kind="checkbox"
              name={question.id}
              isOn={chosen.includes(option.id)}
              label={option.text}
              onChange={(isOn) => saving.change(question.id, { kind: 'choice', optionIds: isOn ? [...chosen, option.id] : chosen.filter((id) => id !== option.id) }, false)}
            />
          ))}
        </div>
      )}
      {question.type === 'text' && (
        <Input
          aria-labelledby={labelId}
          value={text}
          maxLength={SHORT_ANSWER_MAX}
          autoComplete="off"
          onChange={(event) => saving.change(question.id, { kind: 'text', text: event.target.value }, true)}
          onBlur={() => void saving.flush()}
        />
      )}
      {question.type === 'long_text' && (
        <div className={styles.longAnswer}>
          <Textarea
            aria-labelledby={labelId}
            rows={6}
            value={text}
            maxLength={LONG_ANSWER_MAX}
            counterFrom={LONG_ANSWER_MAX - 2000}
            onChange={(event) => saving.change(question.id, { kind: 'text', text: event.target.value }, true)}
            onBlur={() => void saving.flush()}
          />
        </div>
      )}
      {question.type === 'file' && <FileAnswer token={token} question={question} />}
      <SaveNote state={saving.states.get(question.id)} refusal={saving.refusals.get(question.id)} />
    </fieldset>
  );
}

/**
 * One answer to pick, as a full-width card (Test-player mockup): the whole card is the target, and a
 * chosen one takes the brand tint and ring. The native input stays, off-screen, so the keyboard and
 * a screen reader work as with any radio or tick box.
 */
function ChoiceCard({ kind, name, isOn, label, onChange }: { readonly kind: 'radio' | 'checkbox'; readonly name: string; readonly isOn: boolean; readonly label: string; readonly onChange: (isOn: boolean) => void }): ReactNode {
  return (
    <label className={`${styles.option} ${isOn ? styles.optionOn : ''}`}>
      <input type={kind} name={name} checked={isOn} className={styles.hiddenInput} onChange={(event) => onChange(event.target.checked)} />
      <span className={kind === 'radio' ? styles.dot : styles.tick} aria-hidden>
        {kind === 'checkbox' && <Check size={14} strokeWidth={3} />}
      </span>
      <span className={styles.optionText}>{label}</span>
    </label>
  );
}

function FileAnswer({ token, question }: { readonly token: string; readonly question: TestQuestionView }): ReactNode {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState(question.file);
  const [isUploading, setIsUploading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function upload(chosen: File | undefined): Promise<void> {
    if (!chosen) return;
    setProblem(null);
    if (chosen.size > ASSESSMENT_FILE_MAX_BYTES) {
      setProblem('File is too large. Maximum 10 MB.');
      return;
    }
    setIsUploading(true);
    try {
      await uploadTestAnswer(token, question.id, chosen);
      setFile({ name: chosen.name, bytes: chosen.size });
      // The answered count reads the server's copy; a refetch of the same section keeps what is typed.
      void queryClient.invalidateQueries({ queryKey: testSessionKey(token) });
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setIsUploading(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className={styles.file}>
      {/* The native picker is hidden; our button opens it (CLAUDE.md §5). */}
      <input ref={input} type="file" accept={ASSESSMENT_FILE_ACCEPT} className={styles.hiddenInput} onChange={(event) => void upload(event.target.files?.[0])} />
      <Button variant="outline" icon={FileUp} isPending={isUploading} onClick={() => input.current?.click()}>
        {file ? 'Replace the file' : 'Upload a file'}
      </Button>
      {file ? <span className={styles.saved}>Saved: {file.name}</span> : <span className={styles.meta}>{ASSESSMENT_FILE_RULE}</span>}
      {problem && <p className={styles.error}>{problem}</p>}
    </div>
  );
}

function SaveNote({ state, refusal }: { readonly state: SaveState | undefined; readonly refusal: string | undefined }): ReactNode {
  if (state === 'refused') {
    return (
      <p className={styles.error}>
        <AlertTriangle size={14} aria-hidden /> {refusal ?? 'This answer could not be saved.'}
      </p>
    );
  }
  if (state === 'saved') return <p className={styles.saved}>Saved</p>;
  if (state === 'saving') return <p className={styles.meta}>Saving…</p>;
  if (state === 'waiting') return <p className={styles.meta}>Not saved yet — it will be in a moment</p>;
  return null;
}

// --- Frame and notices ---------------------------------------------------------------

function Frame({ company, children }: { readonly company?: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className={styles.frame}>
      <main className={styles.column}>
        {company && <p className={styles.brand}>{company}</p>}
        {children}
      </main>
    </div>
  );
}

function Notice({
  icon: Icon,
  title,
  body,
  action,
  tone,
}: {
  readonly icon: typeof Clock;
  readonly title: string;
  readonly body: string;
  readonly action?: ReactNode;
  readonly tone?: 'success';
}): ReactNode {
  return (
    <Frame>
      <section className={`${styles.card} ${styles.notice}`}>
        <Icon size={32} aria-hidden className={tone === 'success' ? styles.noticeIconSuccess : styles.noticeIcon} />
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>{body}</p>
        {action}
      </section>
    </Frame>
  );
}

function clockToneOf(secondsLeft: number | null): string {
  if (secondsLeft === null) return '';
  if (secondsLeft <= URGENT_SECONDS) return styles.clockUrgent ?? '';
  if (secondsLeft <= WARN_SECONDS) return styles.clockWarn ?? '';
  return '';
}

function isAnswered(answer: TestAnswer | null, question: TestQuestionView): boolean {
  if (question.type === 'file') return question.file !== null;
  if (!answer) return false;
  return answer.kind === 'choice' ? answer.optionIds.length > 0 : answer.text.trim() !== '';
}

function useOnline(): boolean {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = (): void => setIsOnline(true);
    const off = (): void => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return isOnline;
}
